import * as i18nIsoCountries from 'i18n-iso-countries';
import * as _ from 'lodash';
import { BehaviorSubject, combineLatest, Observable, Subject } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, filter, map, switchMap } from 'rxjs/operators';

import { Component, EventEmitter, Input, OnInit, Output, ViewEncapsulation } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';

import { LanguageService } from 'services';

import { AuthenticationModel, SearchOrganizationResponse } from '../../../../../authentication.model';
import { hasCyrillicCharactersValidator } from '../../../../../signup/helpers/form-validators';

@Component({
    selector: 'fc-organization-info-form',
    templateUrl: './organization-info-form.component.html',
    styleUrls: [
        './organization-info-form.component.less',
        '../../../../../../shared/styles/components/fc-guestpanel.less'
    ],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class OrganizationInfoFormComponent implements OnInit {
    @Input() organizationType: 'retailer' | 'manufacturer';
    @Input() showPrevButton = true;

    @Output() organizationLookupClicked = new EventEmitter<{ step: number; organization?: any }>();
    @Output() organizationFormSubmitted = new EventEmitter<FormGroup>();
    @Output() backClicked = new EventEmitter<number>();

    public organizationForm: FormGroup;
    public isFormSubmitted: Subject<boolean> = new Subject();
    public countries: Observable<{ value: string; label: string }[]>;
    public languages: { value: string; viewValue: string }[];
    public organizationNameValue: string;
    public organizationLookupResult: Observable<{ label: string; value: SearchOrganizationResponse }[]>;
    public organizationLookupCompleted: Observable<boolean>;

    private _organizationLookupCompleted = new BehaviorSubject<boolean>(false);
    private organizationNameSearchQuery: BehaviorSubject<string> = new BehaviorSubject<string>('');
    private countrySearchQuery: BehaviorSubject<string> = new BehaviorSubject<string>('');
    private topCountries: i18nIsoCountries.LocalizedCountryNames;
    public referralId: string;

    constructor(
        private languageService: LanguageService,
        private authenticationModel: AuthenticationModel,
        private route: ActivatedRoute,
        formBuilder: FormBuilder
    ) {
        this.topCountries = {
            BE: 'Belgium',
            DK: 'Denmark',
            DE: 'Germany',
            NL: 'Netherlands',
            GB: 'United Kingdom'
        };
        this.organizationLookupCompleted = this._organizationLookupCompleted.asObservable();
        this.organizationForm = formBuilder.group({
            organizationName: new FormControl(this.organizationNameValue, [
                Validators.required,
                hasCyrillicCharactersValidator()
            ]),
            organizationPhone: new FormControl('', [Validators.required, Validators.pattern('[- +()0-9]+')]),
            organizationLine1: new FormControl('', [Validators.required, hasCyrillicCharactersValidator()]),
            organizationZip: new FormControl('', [Validators.required]),
            organizationCity: new FormControl('', [Validators.required, hasCyrillicCharactersValidator()]),
            organizationCountry: new FormControl({ value: 'de', label: 'Germany' }, [Validators.required]),
            signupReason: new FormControl('')
        });
        this.referralId = this.route.snapshot.paramMap.get('referralId');
    }

    ngOnInit(): void {
        const newOrganizationTranslationObservable = this.languageService.currentLanguage.pipe(
            switchMap(() =>
                this.languageService.translateObservable(
                    this.organizationType === 'retailer'
                        ? 'SIGNUP.ORGANIZATION_LOOKUP.DROPDOWN.NEW_RETAILER'
                        : 'SIGNUP.ORGANIZATION_LOOKUP.DROPDOWN.NEW_MANUFACTURER'
                )
            )
        );
        const organizations = this.organizationNameSearchQuery.pipe(
            filter((name) => !_.isEmpty(name)),
            debounceTime(400),
            switchMap((name) => this.authenticationModel.searchOrganization({ type: this.organizationType, name }))
        );

        this.organizationLookupResult = combineLatest([newOrganizationTranslationObservable, organizations]).pipe(
            map(([translation, organizations]) => [
                ...organizations.map((org) => this.formatOrganizationLookupResult(org)),
                {
                    label: translation,
                    value: null,
                    colored: true,
                    action: () => this.organizationLookupClicked.emit({ step: 1 })
                }
            ])
        );

        this.countries = combineLatest([this.languageService.currentLanguage, this.countrySearchQuery]).pipe(
            distinctUntilChanged(),
            map(([language, searchQuery]) => {
                const allCountries = i18nIsoCountries.getNames(language);
                const countries = _.toPairs({ ...this.topCountries, ...allCountries });
                return _.map(
                    _.filter(countries, ([code, name]) => new RegExp(`^${searchQuery}`, 'i').test(name)),
                    ([code, name]) => ({
                        value: _.lowerCase(code),
                        label: name
                    })
                );
            }),
            catchError((error) => [])
        );
    }

    public getErrorTranslationKey(form: FormGroup, inputName: string, fallbackKey: string) {
        const isCyrillicError = form.controls[inputName].errors && form.controls[inputName].errors.cyrillic;

        return isCyrillicError ? 'SIGNUP.FORM.VALIDATION.HAS_CYRILLIC' : fallbackKey;
    }

    public submitOrganization(organizationForm: FormGroup) {
        // Update organization name value in case user didn't click 'Create a new retailer' selection after changing the name
        this.updateOrganizationName();

        this.organizationFormSubmitted.emit(organizationForm);
    }

    public onSearchQueryChange(name: string, field: 'organizationName' | 'country') {
        if (field === 'organizationName') {
            this.organizationNameSearchQuery.next(name);
            // Invalidate form value if it is empty
            if (name === '') this.updateOrganizationName();
            return;
        }
        this.countrySearchQuery.next(name);
    }

    public onOrganizationNameChange(item) {
        if (!item) return;
        // Change the value only if 'create a new retailer' selected
        if (item.value !== null) return;
        this.updateOrganizationName();
        this._organizationLookupCompleted.next(true);
    }

    public isFieldInvalid(form: FormGroup, field: string): boolean {
        return form.controls[field].invalid && (form.controls[field].dirty || form.controls[field].touched);
    }

    onBackClicked() {
        if (!!this._organizationLookupCompleted.value) {
            return this._organizationLookupCompleted.next(false);
        }
        this.backClicked.emit(0);
    }

    private updateOrganizationName() {
        const searchQuery = this.organizationNameSearchQuery.getValue();
        const organizationNameField = this.organizationForm.get('organizationName');
        if (searchQuery === '') {
            organizationNameField.reset();
            return;
        }
        organizationNameField.patchValue({
            label: searchQuery,
            value: searchQuery
        });
    }

    private formatOrganizationLookupResult(organization: SearchOrganizationResponse) {
        const formatManufacturerLabel = (manufacturer: SearchOrganizationResponse) =>
            _.has(manufacturer, 'matchedBrand')
                ? `${manufacturer.matchedBrand}, part of ${manufacturer.name}`
                : manufacturer.name;
        const formatRetailerLabel = (retailer: SearchOrganizationResponse) =>
            `${retailer.name}, ${retailer.address?.line1} ${retailer.address?.zip} ${retailer.address?.city}`;
        const label =
            this.organizationType === 'manufacturer'
                ? formatManufacturerLabel(organization)
                : formatRetailerLabel(organization);
        return {
            label,
            value: organization,
            action: () => this.organizationLookupClicked.emit({ step: -1, organization })
        };
    }
}
